/**
 * Admin Appointments page — v8-7-2
 * Lists all branch appointments with filters; click a row to view detail panel.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Calendar, ChevronDown, ChevronUp, FileText, CheckCircle2, Clock, X,
} from 'lucide-react';
const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8001';

function authHeaders() {
  const token = localStorage.getItem('token') ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers: authHeaders() });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
}

interface AdminBookingListItem {
  id: string;
  user_id: number;
  client_name: string;
  room_name: string;
  appointment_at: string;
  agenda: string | null;
  status: string;
  session_status: string | null;
  action_items_open_count: number;
  has_pdf: boolean;
}

interface ActionItem {
  id: string;
  action: string;
  owner: string;
  due_date: string | null;
  completed: boolean;
  admin_notes: string | null;
}

interface TranscriptMessage {
  role: string;
  content: string;
  created_at: string | null;
}

interface AdminBookingDetail extends AdminBookingListItem {
  banker_prep_notes: string | null;
  fais_acknowledged_at: string | null;
  session_joined_at: string | null;
  session_ended_at: string | null;
  transcript: TranscriptMessage[];
  action_items: ActionItem[];
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'text-blue-400',
  active: 'text-green-400',
  completed: 'text-gray-400',
  cancelled: 'text-red-400 opacity-60',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

function BookingDetailPanel({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [prepOpen, setPrepOpen] = useState(false);

  const { data, isLoading } = useQuery<AdminBookingDetail>({
    queryKey: ['admin-booking-detail', bookingId],
    queryFn: () => apiFetch<AdminBookingDetail>(`/api/v1/bookings/admin/bookings/${bookingId}`),
  });

  const markDone = useMutation({
    mutationFn: ({ itemId, notes }: { itemId: string; notes?: string }) =>
      apiFetch(`/api/v1/bookings/admin/bookings/${bookingId}/action-items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ admin_notes: notes ?? null }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-booking-detail', bookingId] }),
  });

  const roleColor = (role: string) =>
    role === 'client' ? 'text-blue-400' : role === 'banker' ? 'text-amber-400' : 'text-gray-400';

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[600px] bg-gray-900 border-l border-gray-700 shadow-2xl z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 flex-shrink-0">
        <h2 className="text-lg font-semibold text-white">Booking Detail</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        </div>
      )}

      {data && (
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-sm text-gray-300">
          {/* Metadata */}
          <div className="bg-gray-800 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Client</span>
              <span className="text-white">{data.client_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Room</span>
              <span>{data.room_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Date</span>
              <span>{formatDate(data.appointment_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Status</span>
              <span className={STATUS_COLORS[data.status] ?? 'text-gray-400'}>{data.status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Agenda</span>
              <span className="text-right max-w-xs">{data.agenda ?? '—'}</span>
            </div>
            {data.fais_acknowledged_at && (
              <div className="flex justify-between">
                <span className="text-gray-400">FAIS Acknowledged</span>
                <span>{formatDate(data.fais_acknowledged_at)}</span>
              </div>
            )}
          </div>

          {/* Banker Prep Notes — AC:8 admin-only */}
          <div>
            <button
              className="flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium w-full"
              onClick={() => setPrepOpen((v) => !v)}
            >
              <FileText className="h-4 w-4" />
              Banker Prep Notes
              {prepOpen ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
            </button>
            {prepOpen && (
              <div className="mt-2 bg-gray-800 rounded-lg p-3 text-gray-300 whitespace-pre-wrap text-xs leading-relaxed">
                {data.banker_prep_notes ?? 'No prep notes recorded.'}
              </div>
            )}
          </div>

          {/* Transcript */}
          <div>
            <h3 className="text-gray-400 uppercase text-xs font-semibold tracking-wide mb-2">Meeting Transcript</h3>
            <div className="bg-gray-800/60 rounded-lg p-3 space-y-1.5 max-h-64 overflow-y-auto">
              {data.transcript.length === 0 ? (
                <p className="text-gray-500 text-xs">No transcript recorded.</p>
              ) : (
                data.transcript.map((m, i) => (
                  <p key={i}>
                    <strong className={roleColor(m.role)}>[{m.role.toUpperCase()}]</strong>{' '}
                    {m.content}
                  </p>
                ))
              )}
            </div>
          </div>

          {/* Action Items */}
          <div>
            <h3 className="text-gray-400 uppercase text-xs font-semibold tracking-wide mb-2">Action Items</h3>
            {data.action_items.length === 0 ? (
              <p className="text-gray-500 text-xs">No action items.</p>
            ) : (
              <div className="space-y-2">
                {data.action_items.map((item) => (
                  <div
                    key={item.id}
                    className={`bg-gray-800 rounded-lg p-3 flex items-start justify-between gap-3 ${item.completed ? 'opacity-60' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={item.completed ? 'line-through text-gray-500' : ''}>{item.action}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.owner} · {item.due_date ?? 'No due date'}
                      </p>
                      {item.admin_notes && (
                        <p className="text-xs text-amber-400/80 mt-1">📝 {item.admin_notes}</p>
                      )}
                    </div>
                    {!item.completed && (
                      <button
                        onClick={() => markDone.mutate({ itemId: item.id })}
                        disabled={markDone.isPending}
                        className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 whitespace-nowrap"
                      >
                        <CheckCircle2 className="h-4 w-4" /> Mark Done
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AdminAppointments() {
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-bookings', statusFilter, dateFrom, dateTo],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('date_to', new Date(dateTo).toISOString());
      return apiFetch<{ items: AdminBookingListItem[] }>(`/api/v1/bookings/admin/bookings?${params}`);
    },
  });

  const items: AdminBookingListItem[] = data?.items ?? [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center gap-3">
        <Calendar className="h-7 w-7 text-amber-400" />
        <h1 className="text-2xl font-bold text-white">Branch Appointments</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="pending">Upcoming</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="From"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="To"
        />
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400 text-sm">
          Failed to load appointments.
        </div>
      )}

      {!isLoading && !error && (
        <div className="overflow-x-auto rounded-xl border border-gray-700">
          <table className="w-full text-sm text-gray-300">
            <thead>
              <tr className="bg-gray-800/60 text-gray-400 uppercase text-xs tracking-wide">
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">Room</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Session</th>
                <th className="px-4 py-3 text-right">Open Actions</th>
                <th className="px-4 py-3 text-center">PDF</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No appointments found.</td>
                </tr>
              ) : (
                items.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => setSelectedId(b.id)}
                    className={`border-t border-gray-700/50 hover:bg-gray-800/40 cursor-pointer transition-colors ${
                      b.status === 'cancelled' ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(b.appointment_at)}</td>
                    <td className="px-4 py-3">{b.client_name}</td>
                    <td className="px-4 py-3">{b.room_name}</td>
                    <td className={`px-4 py-3 font-medium ${STATUS_COLORS[b.status] ?? 'text-gray-400'}`}>
                      {b.status}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{b.session_status ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {b.action_items_open_count > 0 ? (
                        <span className="text-amber-400 font-semibold">{b.action_items_open_count}</span>
                      ) : (
                        <span className="text-gray-600">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {b.has_pdf ? (
                        <FileText className="h-4 w-4 text-green-400 mx-auto" />
                      ) : (
                        <Clock className="h-4 w-4 text-gray-600 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <BookingDetailPanel bookingId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}
